import * as React from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";

/**
 * Regular expressions for matching Roboto resource IDs in URLs.
 *
 * Dataset IDs: ds_<12 base36 chars> (e.g., ds_fkb5h2bbwruf)
 * File IDs: fl_<20 base36 chars> (e.g., fl_xjr6tzvx9ctok7fmn7z4)
 *
 * Base36 character set: [0-9a-z]
 */
const DATASET_ID_REGEX = /ds_[0-9a-z]{12}/g;
const FILE_ID_REGEX = /fl_[0-9a-z]{20}/g;

/**
 * Extracts unique dataset and file IDs from a given text string.
 *
 * @param text - The text to search for IDs (e.g., URL path or query string)
 * @returns An object containing arrays of unique dataset_ids and file_ids
 */
function extractIdsFromText(text: string): {
  dataset_ids: string[];
  file_ids: string[];
} {
  const datasetMatches = text.match(DATASET_ID_REGEX) || [];
  const fileMatches = text.match(FILE_ID_REGEX) || [];

  // Use Set to deduplicate IDs
  const dataset_ids = Array.from(new Set(datasetMatches));
  const file_ids = Array.from(new Set(fileMatches));

  return { dataset_ids, file_ids };
}

/**
 * Hook that generates RobotoLLMContext by extracting dataset and file IDs
 * from the current URL (both pathname and query string).
 *
 * This hook automatically updates when the URL changes, providing dynamic
 * context based on the user's current location in the application.
 *
 * @returns RobotoLLMContext containing dataset_ids and file_ids found in the URL
 *
 * @example
 * // On /datasets/ds_fkb5h2bbwruf/files
 * const context = useRobotoLLMContextFromUrl();
 * // Returns: { dataset_ids: ["ds_fkb5h2bbwruf"], file_ids: [] }
 *
 * @example
 * // On /files/fl_xjr6tzvx9ctok7fmn7z4
 * const context = useRobotoLLMContextFromUrl();
 * // Returns: { dataset_ids: [], file_ids: ["fl_xjr6tzvx9ctok7fmn7z4"] }
 *
 * @example
 * // On /search?dataset=ds_abc123456789&file=fl_12345678901234567890
 * const context = useRobotoLLMContextFromUrl();
 * // Returns: { dataset_ids: ["ds_abc123456789"], file_ids: ["fl_12345678901234567890"] }
 */
export function useRobotoLLMContextFromUrl(): RobotoLLMContext {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const context = React.useMemo(() => {
    // Combine pathname and search params into a single string to search
    const pathname = location.pathname;
    const search = searchParams.toString();
    const fullUrl = `${pathname}?${search}`;

    // Extract IDs from the combined URL
    const { dataset_ids, file_ids } = extractIdsFromText(fullUrl);

    return {
      dataset_ids,
      file_ids,
    };
  }, [location.pathname, searchParams]);

  return context;
}
